import { useSearchParams } from 'react-router-dom'
import { getDateRangeForMode } from '../../components/DateFilterBar'
import { useVehicleFilter } from '../../contexts/VehicleFilterContext'
import type { PostnummerDirection } from '../../components/PostnummerFilter'
import { useMinePostnummerInfo } from '../../hooks/useMinePostnummer'

export type BruktbilFilters = {
  from: string
  to: string
  /**
   * True when the user has committed a period, i.e. from/to are in the URL.
   *
   * The period is in the URL; the MODE that produced it is not, so this is the
   * only honest thing a consumer can know about it. It decides whether the
   * displayed end date may be clipped to the data cutoff (#594 AC4): an
   * explicit period is already exactly what the user asked for, while the
   * implicit default below runs to today, past where data actually goes.
   */
  periodExplicit: boolean
  klasse: string[]
  // 3-state vehicle filter, matching the total-statistics convention:
  //   undefined -> alle, false -> personbil only, true -> varebil only.
  varebil: boolean | undefined
  omregistrering: boolean
  tab: string | undefined
  merke: string[]
  modell: string[]
  aarMin: number | undefined
  aarMax: number | undefined
  // Postnummer filter state
  postnummerEnabled: boolean
  postnummerDirection: PostnummerDirection
  // Resolved postnummer values for API calls (null = no filter)
  selgerPostnummer: string | null
  /** Name of the active markedsområde, or null when none (#527). */
  postnummerNavn: string | null
  kjoperPostnummer: string | null
}

type SetFilters = (partial: Partial<Omit<BruktbilFilters, 'varebil' | 'selgerPostnummer' | 'kjoperPostnummer'>>) => void

export function useBruktbilFilters(): BruktbilFilters & { setFilters: SetFilters } {
  const [searchParams, setSearchParams] = useSearchParams()
  const { getVarebilParam } = useVehicleFilter()

  // Note this resolves WITHOUT a maxDate, so the implicit default `to` is
  // today — later than the data goes. That gap is what the explanation box's
  // cutoff clipping is for, and why periodExplicit has to gate it (#594 AC4).
  const defaultRange = getDateRangeForMode('year_to_date')
  const from = searchParams.get('from') ?? defaultRange.startDate
  const to = searchParams.get('to') ?? defaultRange.endDate
  const periodExplicit = searchParams.has('from') || searchParams.has('to')
  const klasse = searchParams.getAll('klasse')
  // 3-state: undefined = alle, false = personbil only, true = varebil only.
  // The backend reads the same convention (GlobalFilters.varebil).
  const varebil = getVarebilParam()
  const omregistrering: boolean = searchParams.get('omregistrering') === 'true'
  const tabRaw = searchParams.get('tab')
  const tab = tabRaw !== null ? tabRaw : undefined

  // Merke/Modell module-local filter state (Sammenligning tab).
  const merke = searchParams.getAll('merke')
  const modell = searchParams.getAll('modell')
  const aarMinRaw = searchParams.get('aarMin')
  const aarMaxRaw = searchParams.get('aarMax')
  const aarMin = aarMinRaw !== null ? Number(aarMinRaw) : undefined
  const aarMax = aarMaxRaw !== null ? Number(aarMaxRaw) : undefined

  // Postnummer filter
  const postnummerEnabled = searchParams.get('geo') === '1'
  const postnummerDirection = (searchParams.get('geoDir') ?? 'begge') as PostnummerDirection
  const { postnummer: resolvedPostnummer, navn: postnummerNavn } =
    useMinePostnummerInfo(postnummerEnabled)

  // Resolve direction to selger/kjoper postnummer for API calls
  let selgerPostnummer: string | null = null
  let kjoperPostnummer: string | null = null
  if (resolvedPostnummer) {
    if (postnummerDirection === 'bilselger' || postnummerDirection === 'begge') {
      selgerPostnummer = resolvedPostnummer
    }
    if (postnummerDirection === 'bilkjøper' || postnummerDirection === 'begge') {
      kjoperPostnummer = resolvedPostnummer
    }
  }

  function setFilters(partial: Partial<Omit<BruktbilFilters, 'varebil' | 'selgerPostnummer' | 'kjoperPostnummer'>>) {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if ('from' in partial && partial.from !== undefined) next.set('from', partial.from)
      if ('to' in partial && partial.to !== undefined) next.set('to', partial.to)
      if ('klasse' in partial) {
        next.delete('klasse')
        for (const k of partial.klasse ?? []) next.append('klasse', k)
      }
      if ('omregistrering' in partial) {
        if (partial.omregistrering) next.set('omregistrering', 'true')
        else next.delete('omregistrering')
      }
      if ('tab' in partial) {
        if (partial.tab !== undefined) next.set('tab', partial.tab)
        else next.delete('tab')
      }
      if ('merke' in partial) {
        next.delete('merke')
        for (const m of partial.merke ?? []) next.append('merke', m)
      }
      if ('modell' in partial) {
        next.delete('modell')
        for (const m of partial.modell ?? []) next.append('modell', m)
      }
      if ('aarMin' in partial) {
        if (partial.aarMin !== undefined) next.set('aarMin', String(partial.aarMin))
        else next.delete('aarMin')
      }
      if ('aarMax' in partial) {
        if (partial.aarMax !== undefined) next.set('aarMax', String(partial.aarMax))
        else next.delete('aarMax')
      }
      if ('postnummerEnabled' in partial) {
        if (partial.postnummerEnabled) next.set('geo', '1')
        else next.delete('geo')
      }
      if ('postnummerDirection' in partial) {
        if (partial.postnummerDirection && partial.postnummerDirection !== 'begge') next.set('geoDir', partial.postnummerDirection)
        else next.delete('geoDir')
      }
      return next
    })
  }

  return { from, to, periodExplicit, klasse, varebil, omregistrering, tab, merke, modell, aarMin, aarMax, postnummerEnabled, postnummerDirection, selgerPostnummer, kjoperPostnummer, postnummerNavn, setFilters }
}
